import moment from "moment";
import assign from "lodash.assign";
import { Set } from "immutable";
import {
  redirectToRoute,
  buildFormErrors,
  castToNull,
} from "businessLogic/util";
import { getSelectedCustomerId } from "selectors/customerSelectors";
import { getAllLocations } from "selectors/locationSelectors";
import * as types from "../constants/actionTypes";
import {
  addErrorNotification,
  addSuccessNotification,
} from "../actions/notificationActions";
import { setSelectedCustomer } from "../actions/customerActions";
import { fetchLocationUserStatus } from "../actions/usersActions";
import { addToStorage } from "businessLogic/localStorage";
import { FORM_ERROR } from "final-form";

// todo: missing unit test
export function fetchLocation(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const location = getAllLocations(getState()).get(locationId);

    if (
      isLocationFetching(getState(), locationId) ||
      (location && locationFetchedRecently(location))
    ) {
      return Promise.resolve();
    }

    dispatch({ type: types.LOCATION_FETCHING, payload: locationId });

    const api = APIFactory.getInstance(getState());
    return api
      .get(`/callcenter/location/${locationId}`)
      .then((response) => {
        dispatch({
          type: types.LOCATION_FETCHED,
          payload: { locationId, response: response.data },
        });
      })
      .catch(() => {
        dispatch({ type: types.LOCATION_FETCH_ERROR, payload: { locationId } });
      });
  };
}

// Live-looks-up a location's SMS number in Twilio to find its own SID (not stored
// on the location — see CallCenter/LocationController::getTwilioNumberSid()), so the
// SMS Settings tab can link "Twilio SMS Number" to that number's own console page.
// Ephemeral, tab-specific data — not dispatched into the location reducer.
export function fetchTwilioNumberSid(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/callcenter/location/${locationId}/twilio-number-sid`)
      .then((response) => response.data.sid)
      .catch(() => null);
  };
}

// Resolves a location's owning customer and selects it, fetching the location
// first only if it isn't already in state (e.g. a super-admin deep link to a
// location whose customer was never preloaded via fetchCustomerLocations).
export function setSelectedCustomerFromLocation(locationId) {
  return (dispatch, getState) => {
    const existingCustomerId = getAllLocations(getState())
      .get(locationId)?.get("customerId");

    if (existingCustomerId) {
      return Promise.resolve(dispatch(setSelectedCustomer(existingCustomerId)));
    }

    return dispatch(fetchLocation(locationId)).then(() => {
      const location = getAllLocations(getState()).get(locationId);
      const customerId = location && location.get("customerId");
      if (customerId) {
        return dispatch(setSelectedCustomer(customerId));
      }
      return undefined;
    });
  };
}

function isLocationFetching(state, locationId) {
  return state.locations.hasIn(["fetching", locationId]);
}

function locationFetchedRecently(location, maxSeconds = 120) {
  if (!location.get("lastFetched")) return false;
  const secondsOld = Math.abs(
    location.get("lastFetched").diff(moment(), "seconds")
  );
  return secondsOld < maxSeconds;
}

export function setSelectedLocation(locationId) {
  return (dispatch) => {
    dispatch({ type: types.SET_SELECTED_LOCATION, payload: locationId });
    // Skip persistence when clearing the selection so we don't overwrite a valid stored id with null.
    if (locationId) {
      addToStorage({ selectedLocationId: locationId });
    }
    return locationId;
  };
}

export function fetchCustomerLocations(customerId) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({
      type: types.CUSTOMER_LOCATIONS_FETCHING,
      payload: { customerId },
    });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/customer/${customerId}/locations`)
      .then((response) => {
        dispatch({
          type: types.CUSTOMER_LOCATIONS_FETCHED,
          payload: { customerId, locations: response.data.locations },
        });
      })
      .catch(() => {
        dispatch({
          type: types.CUSTOMER_LOCATIONS_FETCH_ERROR,
          payload: { customerId },
        });
      });
  };
}

export function fetchCustomerLocationStatus(customerId) {
  return (dispatch, getState) => {
    const locationIds = getState().customers.getIn(
      ["customers", customerId, "locationIds"],
      Set()
    );
    const promises = locationIds.map((locationId) =>
      dispatch(fetchLocationUserStatus(locationId))
    );
    return Promise.all(promises.toArray());
  };
}

export function fetchSelectedCustomerLocationStatus() {
  return (dispatch, getState) =>
    dispatch(fetchCustomerLocationStatus(getSelectedCustomerId(getState())));
}

export function fetchLocationDealerMessages(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({
      type: types.LOCATION_DEALER_MESSAGES_FETCHING,
      payload: { locationId },
    });

    const api = APIFactory.getInstance(getState());
    return api
      .get(`/location/${locationId}/dealermessages`)
      .then((response) => {
        dispatch({
          type: types.LOCATION_DEALER_MESSAGES_FETCHED,
          payload: { locationId, messages: response.data.items },
        });
      })
      .catch(() => {
        dispatch({
          type: types.LOCATION_DEALER_MESSAGES_FETCH_ERROR,
          payload: { locationId },
        });
        dispatch(
          addErrorNotification("Error fetching messages from this dealer.")
        );
      });
  };
}

export function deactivateLocation(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.LOCATION_DELETING });

    const api = APIFactory.getInstance(getState());
    return api
      .delete(`/admin/location/${locationId}`)
      .then(() => {
        dispatch({ type: types.LOCATION_DELETED, payload: { locationId } });
      })
      .catch(() => {
        dispatch({
          type: types.LOCATION_DELETE_ERROR,
          payload: { locationId },
        });
        dispatch(addErrorNotification("Error deactivating location"));
      });
  };
}
/**
 * Create a new location
 * 3 step process walking down the promise chain:
 *  1. create location
 *  2. update location settings
 *  3. upload avatar (optional)
 * @param formData
 */
export function createLocation(formData, reloadOnSubmit) {
  return (dispatch, getState, { APIFactory }) => {
    let locationId;
    const locationPayload = buildLocationPayload(formData);

    dispatch({ type: types.LOCATION_UPDATING });

    const api = APIFactory.getInstance(getState());
    return api
      .post("/admin/location", locationPayload) // save location
      .then((response) => {
        locationId = response.data.id;
        const guid = response.data.guid;
        dispatch({
          type: types.LOCATION_SAVED,
          payload: assign({}, locationPayload, { id: locationId, guid }),
        });
      })
      .then(() => {
        // save location settings
        const locationSettingsPayload = buildLocationSettingsPayload(formData);
        return api
          .patch(
            `/admin/location/${locationId}/settings`,
            locationSettingsPayload
          )
          .then(() => {
            dispatch({
              type: types.LOCATION_SETTINGS_SAVED,
              payload: assign({}, locationSettingsPayload, { locationId }),
            });
          });
      })
      .then(() => {
        // save avatar if it exists
        if (formData.avatar && formData.avatar[0]) {
          return saveAvatar(api, dispatch, locationId, formData.avatar);
        } else {
          // since they didn't upload an avatar, we need to use the customer's avatar instead
          const customerAvatar = getState().customers.getIn([
            "customers",
            formData.customerId,
            "avatar",
          ]);
          dispatch({
            type: types.LOCATION_AVATAR_SAVED,
            payload: {
              locationId,
              avatar: customerAvatar,
              avatarType: "customer",
            },
          });
          return Promise.resolve();
        }
      })
      .then(() => {
        dispatch({ type: types.LOCATION_UPDATED });
        if (reloadOnSubmit) {
          dispatch({ type: types.SET_SELECTED_LOCATION, payload: locationId });
          redirectToRoute("/admin/location/edit");
        } else {
          redirectToRoute("/admin/location");
        }
      })
      .catch((error) => {
        dispatch({ type: types.LOCATION_UPDATE_ERROR });
        return buildFormErrors("Error creating location.", error.response);
      });
  };
}

function saveAvatar(api, dispatch, locationId, avatar) {
  const data = new window.FormData();
  data.append("avatar", avatar[0]);
  return api
    .post(`/admin/location/${locationId}/avatar`, data)
    .then((response) => {
      return Promise.resolve(response);
    })
    .then((response) =>
      dispatch({
        type: types.LOCATION_AVATAR_SAVED,
        payload: {
          locationId,
          avatar: response.data.avatar,
          avatarType: "location",
        },
      })
    );
}

function buildLocationPayload(formData) {
  const {
    customerId,
    name,
    url,
    contactEmail,
    contactName,
    timezone,
    phoneNumber,
    smsPhoneNumber,
    youtubeChannelId,
    diDashboardKey,
    slug,
    address,
    city,
    state,
    country,
    postcode,
    offlineMessage,
    billable,
    facebookPageId,
    facebookPageAccessToken,
    gaPropertyId,
    hasDms,
    ccid,
    engineId,
    carsDealerId,
    locationTypeId,
    locationOwnerId,
    oemId,
    ein,
    businessLegalName,
    twilioProfile,
    twilioBrand,
    twilioCampaign,
    twilioMessagingServiceId,
    twilioStatus,
    twilioFailureReason,
  } = formData;

  return {
    customerId,
    name,
    url,
    contactEmail,
    contactName: contactName && contactName.trim(),
    timezone,
    phoneNumber,
    smsPhoneNumber: castToNull(smsPhoneNumber),
    youtubeChannelId: castToNull(youtubeChannelId),
    diDashboardKey: castToNull(diDashboardKey),
    slug: castToNull(slug),
    address,
    city,
    state,
    country,
    postcode,
    offlineMessage,
    billable,
    facebookPageId: castToNull(facebookPageId),
    facebookPageAccessToken: castToNull(facebookPageAccessToken),
    gaPropertyId: castToNull(gaPropertyId),
    hasDms,
    ccid: castToNull(ccid),
    engineId: castToNull(engineId),
    carsDealerId: castToNull(carsDealerId),
    locationOwnerId,
    locationTypeId,
    oemId: castToNull(oemId),
    ein: ein && ein.replace(/[-—]/g, ''),
    businessLegalName: businessLegalName && businessLegalName.trim(),
    twilioProfile,
    twilioBrand,
    twilioCampaign,
    twilioMessagingServiceId,
    twilioStatus,
    twilioFailureReason,
  };
}

function buildLocationSettingsPayload(formData) {
  const {
    requireVisitorFields,
    enableManagedOverage,
    managedAllotment,
    enableManagedChat,
    unlimitedManaged,
    offlineHide,
    videoEnabled,
    bypassCustomizePanel,
    defaultFilterKeyword,
    locale,
    sendAllLeads,
    isVistaDash,
    isClarivoy,
    fbMessengerEnabled,
    fbMarketplaceEnabled,
    isTradePendingEnabled,
    isTradeInEnabled,
    isGptEnabled,
    logUserStatus,
    directToFlow,
    oemDealerType,
    oemDealerCode,
    oemLocationCode,
    oemLocationOSSSubscriber,
    inventoryDealerId,
    cdkWebsiteId,
    customAiContext,
  } = formData;

  return {
    requireVisitorFields: !!requireVisitorFields,
    requiredFields: buildRequiredVisitorFields(formData),
    offlineAction: offlineHide ? "hide" : "message",
    bypassCustomizePanel: !!bypassCustomizePanel,
    defaultFilterKeyword,
    videoEnabled,
    enableManagedOverage,
    enableManagedChat,
    unlimitedManaged,
    managedAllotment,
    locale,
    sendAllLeads,
    isVistaDash,
    isClarivoy,
    fbMessengerEnabled,
    fbMarketplaceEnabled,
    isTradePendingEnabled,
    isTradeInEnabled,
    isGptEnabled,
    logUserStatus,
    directToFlow,
    oemDealerType,
    oemDealerCode,
    oemLocationCode,
    oemLocationOSSSubscriber,
    inventoryDealerId,
    cdkWebsiteId,
    customAiContext,
  };
}

function buildRequiredVisitorFields({
  requireVisitorFields,
  requireFirstName,
  requireLastName,
  requireEmail,
}) {
  if (!!requireVisitorFields !== true) return null;

  const requiredFields = [];

  if (requireFirstName) requiredFields.push("first_name");
  if (requireLastName) requiredFields.push("last_name");
  if (requireEmail) requiredFields.push("email");

  return requiredFields.join(",");
}

export function updateLocation(formData) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.LOCATION_UPDATING });

    const api = APIFactory.getInstance(getState());
    const locationId = formData.locationId;

    // location data
    const locationPayload = buildLocationPayload(formData);
    const locationDataPromise = api
      .patch(`/admin/location/${locationId}`, locationPayload) // save location
      .then(() => {
        dispatch({
          type: types.LOCATION_SAVED,
          payload: assign({}, locationPayload, { id: locationId }),
        });
      })
      .catch(() => {
        throw new Error("Error saving location information.");
      });

    // location settings
    const locationSettingsPromise = updateLocationSettingsPromise(
      formData,
      api,
      dispatch
    );

    // location avatar
    let avatarPromise;
    if (formData.avatar && formData.avatar[0]) {
      avatarPromise = saveAvatar(
        api,
        dispatch,
        locationId,
        formData.avatar
      ).catch(() => {
        // throw buildFormErrors('Error saving avatar.', error.response);
        throw new Error("Error saving avatar.");
      });
    } else {
      avatarPromise = Promise.resolve();
    }

    // run all three API calls in parallel
    return Promise.all([
      locationDataPromise,
      locationSettingsPromise,
      avatarPromise,
    ])
      .then(() => {
        dispatch({ type: types.LOCATION_UPDATED });
        return undefined;
      })
      .catch((error) => {
        dispatch({ type: types.LOCATION_UPDATE_ERROR });
        return { [FORM_ERROR]: error.message };
      });
  };
}

export function updateLocationSettingsPromise(formData, api, dispatch) {
  // location settings
  const locationId = formData.locationId;
  const locationSettingsPayload = buildLocationSettingsPayload(formData);
  return api
    .patch(`/admin/location/${locationId}/settings`, locationSettingsPayload)
    .then(() => {
      dispatch({
        type: types.LOCATION_SETTINGS_SAVED,
        payload: assign({}, locationSettingsPayload, { locationId }),
      });
    })
    .catch((error) => {
      throw buildFormErrors("Error saving location settings.", error.response);
    });
}

export function removeAvatar(locationId, customerId) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.LOCATION_AVATAR_DELETING, payload: { locationId } });

    const customerAvatar = getState().customers.getIn([
      "customers",
      customerId,
      "avatar",
    ]);

    const api = APIFactory.getInstance(getState());
    return api
      .delete(`/admin/location/${locationId}/avatar`)
      .then(() =>
        dispatch({
          type: types.LOCATION_AVATAR_DELETED,
          payload: { locationId, customerAvatar },
        })
      )
      .catch(() => {
        dispatch({
          type: types.LOCATION_AVATAR_DELETE_ERROR,
          payload: { locationId },
        });
        dispatch(addErrorNotification("Error deleting location avatar."));
      });
  };
}

/**
 * Get ALL customers and locations. Can only be used by super admin users.
 */
export function searchAllLocationsAndCustomers(search) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.ALL_LOCATIONS_FETCHING });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/admin/search?query=${search}`)
      .then((response) => {
        dispatch({ type: types.ALL_LOCATIONS_FETCHED });
        return response.data;
      })
      .catch(() => {
        dispatch({ type: types.ALL_LOCATIONS_FETCH_ERROR });
        dispatch(addErrorNotification("Error fetching locations."));
      });
  };
}

export function getCcpaLocations(ccid) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/admin/ccpa/location/${encodeURIComponent(ccid)}`)
      .then((response) => response.data.locations)
      .catch((error) => {
        dispatch(addErrorNotification("Error looking up location name."));
        throw error;
      });
  };
}

export function lookupCcpaRequest(ccid, email) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post("/admin/ccpa/lookup", { ccid, email })
      .then((response) => response.data)
      .catch((error) => {
        dispatch(addErrorNotification("Error looking up CCPA request."));
        throw error;
      });
  };
}

export function purgeCcpaRequest(ccid, email) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post("/admin/ccpa/purge", { ccid, email })
      .then((response) => response.data)
      .catch((error) => {
        dispatch(addErrorNotification("Error redacting CCPA request."));
        throw error;
      });
  };
}

export function updateLocationSettings(locationId, settings) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .patch(`/admin/location/${locationId}/settings`, settings)
      .then(() => {
        dispatch({
          type: types.LOCATION_SETTINGS_SAVED,
          payload: assign({}, settings, { locationId }),
        });
      });
  };
}

export function uploadFile(file) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    const data = new window.FormData();
    data.append("upload", file[0]);

    return api
      .post("/utility/file", data, { timeout: 60000 })
      .then((response) => Promise.resolve(response));
  };
}

/**
 * Creates or cancels an SMS Phone Number.
 */
export function toggleTwilio(locationId, hasTwilio) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    let req;
    if (hasTwilio) {
      req = api.delete(`/admin/twilio?locationId=${locationId}`);
    } else {
      req = api.post("/admin/twilio", { locationId });
    }

    dispatch({ type: types.LOCATION_UPDATING });

    return req
      .then((response) => {
        dispatch({
          type: types.LOCATION_SETTINGS_SAVED,
          payload: assign(
            {},
            { smsPhoneNumber: response.data.smsPhoneNumber },
            { locationId }
          ),
        });
        dispatch({ type: types.LOCATION_UPDATED });
        dispatch(
          addSuccessNotification(
            `Successfully ${
              hasTwilio ? "canceled the" : "created a"
            } Twilio number.`
          )
        );
      })
      .catch(() => {
        dispatch({ type: types.LOCATION_UPDATED });
        dispatch(
          addErrorNotification(
            `Unable to ${hasTwilio ? "cancel this" : "create a"} Twilio number.`
          )
        );
      });
  };
}

/**
 * Resubmits a location to Twilio for verification
 */
export function resubmitTwilio(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    const req = api.post("/admin/resubmit-twilio-verification", { locationId });

    dispatch({ type: types.LOCATION_UPDATING });

    return req
      .then((response) => {
        dispatch({
          type: types.LOCATION_SETTINGS_SAVED,
          payload: assign(
            {},
            {
              twilioCampaignStatus: response.data.twilioCampaignStatus,
              twilioFailureReason: response.data.twilioFailureReason,
            },
            { locationId }
          ),
        });
        dispatch({ type: types.LOCATION_UPDATED });
        dispatch(
          addSuccessNotification("Successfully resubmitted location to Twilio.")
        );
      })
      .catch((err) => {
        dispatch({ type: types.LOCATION_UPDATED });
        dispatch(
          addErrorNotification(`Unable to resubmit this location to Twilio: ${err.response.data.errors}`)
        );
      });
  };
}

/**
 * Completely removes the Twilio integration (profile, brand, campaign, messaging service,
 * and phone number) from a location
 */
export function deleteTwilioIntegration(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    const req = api.post("/admin/delete-twilio-integration", { locationId });

    dispatch({ type: types.LOCATION_UPDATING });

    return req
      .then((response) => {
        dispatch({
          type: types.LOCATION_SETTINGS_SAVED,
          payload: assign({}, response.data.location, { locationId }),
        });
        dispatch({ type: types.LOCATION_UPDATED });
        dispatch(
          addSuccessNotification("Successfully removed Twilio integration from this location.")
        );
      })
      .catch((err) => {
        dispatch({ type: types.LOCATION_UPDATED });
        dispatch(
          addErrorNotification(`Unable to remove Twilio integration: ${err.response.data.errors}`)
        );
      });
  };
}

export function deleteUserFromLocation(api, dispatch, locationId, userId) {
  return api
    .delete('/admin/user/location', { data: { userId, locationId } })
    .then(() => dispatch({ type: types.USER_DELETED_FROM_LOCATION, payload: { location, userId } }));
}

export function addUserToLocation(api, dispatch, locationId, userId) {
  return api
    .post('/admin/user/location', { userId, locationId })
    .then(() => dispatch({ type: types.USER_ADDED_TO_LOCATION, payload: { location, userId } }));
}
